import React, { useEffect, useState } from "react";
import { Star, UserRound } from "lucide-react";
import { api, fmtDate, mediaUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { PageHeader, Spinner, EmptyState, Eyebrow } from "@/components/site/Bits";
import { ContactList } from "@/components/site/Footer";

const GALLERY = [
  { src: "/images/founder-suit.jpg", alt: "Founder in a navy suit" },
  { src: "/images/founder-green.jpg", alt: "Founder smiling in Africa" },
  { src: "/images/founder-a.jpg", alt: "Founder on the move" },
  { src: "/images/founder-b.jpg", alt: "Founder travelling" },
];

const MissionVision = () => {
  const { s } = useSettings();
  const m = s?.mission;
  if (!m?.statement) return null;
  return (
    <section className="relative bg-navy py-20 text-white grain" data-testid="mission-vision">
      <div className="container-x relative grid gap-14 lg:grid-cols-2">
        <div data-testid="mission-statement">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-mint">Mission</p>
          <h2 className="mt-4 text-3xl tracking-tight sm:text-4xl">Built on discipline, integrity, and trust.</h2>
          <div className="mt-6 space-y-4 text-sm leading-relaxed text-white/70 md:text-base">{m.statement.split(/\n\s*\n|\n/).map((p: string, i: number) => <p key={i}>{p}</p>)}</div>
        </div>
        <div data-testid="vision-statement">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-mint">Vision</p>
          <h2 className="mt-4 text-3xl tracking-tight sm:text-4xl">Global, bold, and built to last.</h2>
          <p className="mt-6 text-sm leading-relaxed text-white/70 md:text-base">{m.vision}</p>
          {m.vision_points?.length > 0 && (
            <ul className="mt-6 space-y-3" data-testid="vision-points">
              {m.vision_points.filter((v: string) => v.trim()).map((v: string) => <li key={v} className="flex gap-3 text-sm text-white/80 md:text-base"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#22C55E]" />{v}</li>)}
            </ul>
          )}
        </div>
        {m.closing && <p className="border-t border-white/10 pt-8 font-serif text-xl font-extrabold text-white sm:text-2xl lg:col-span-2" data-testid="mission-closing">{m.closing}</p>}
      </div>
    </section>
  );
};

export const About = () => {
  const { s } = useSettings();
  const f = s?.founder;
  const ready = f?.approved && f?.bio;
  return (
    <div>
      <PageHeader eyebrow="About · Mission & Vision" title={ready && f?.name ? f.name : "From Liberia to the United States."} intro={ready ? f?.title : "From Cavalry Scout to CEO — the story and standards behind Trust Logistics LLC."} testid="about-title" />
      <div className="container-x grid gap-12 py-14 lg:grid-cols-[380px_1fr]">
        <div className="aspect-[4/5] overflow-hidden rounded-3xl bg-white lg:sticky lg:top-24 lg:self-start" data-testid="founder-portrait">
          {f?.portrait_url ? <img src={mediaUrl(f.portrait_url)} alt={f.name || "Founder of Trust Logistics LLC"} className="h-full w-full object-cover object-top" />
            : <div className="flex h-full flex-col items-center justify-center gap-3 bg-gradient-to-br from-navy/5 to-gold/15 text-navy/40"><UserRound size={48} /><span className="font-mono text-[10px] uppercase tracking-widest">Portrait coming soon</span></div>}
        </div>
        <div>
          {ready ? (
            <div data-testid="founder-bio">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold">Meet the founder</p>
              <div className="mt-4 space-y-5 text-base leading-relaxed text-navy/75 md:text-lg">
                {String(f?.bio).split(/\n+/).filter((p) => p.trim()).map((p, i, arr) => (
                  <p key={i} className={i === arr.length - 1 ? "font-serif text-xl font-extrabold leading-snug text-navy sm:text-2xl" : ""}>{p}</p>
                ))}
              </div>
            </div>
          ) : (
            <div data-testid="founder-pending">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold">{f?.title || "Founder & CEO"}</p>
              <p className="mt-3 text-base leading-relaxed text-navy/75 md:text-lg">Trust Logistics LLC operates with military precision, customer-first service, and a global mindset — connecting Africa and the United States through excellence, innovation, and reliability.</p>
              <p className="mt-4 text-sm text-navy/55">The founder's full biography will be published here soon.</p>
            </div>
          )}
          <div className="mt-10 card-lux p-6"><Eyebrow>Contact</Eyebrow><div className="mt-4"><ContactList /></div></div>
          <p className="mt-8 font-serif text-2xl font-extrabold">Delivering Trust.</p>
        </div>
      </div>
      <section className="container-x pb-20" data-testid="army-service">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold">U.S. Army · 19D Cavalry Scout</p>
            <h2 className="mt-3 text-3xl tracking-tight sm:text-4xl">From Cavalry Scout to CEO.</h2>
          </div>
          <p className="max-w-md text-sm text-navy/60">Discipline, leadership and a mission-focused mindset — forged in service, now the operating standard of Trust Logistics LLC.</p>
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((n, i) => (
            <img key={n} src={`/images/army-${n}.jpg`} alt={`Stanley Teddy Gant serving in the U.S. Army (${n})`} loading="lazy" className={`h-72 w-full rounded-2xl object-cover sm:h-96 ${i % 2 ? "md:mt-10" : ""}`} data-testid={`army-photo-${i}`} />
          ))}
        </div>
      </section>
      <MissionVision />
      <section className="container-x py-16" data-testid="founder-gallery">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {GALLERY.map((g, i) => (
            <img key={g.src} src={g.src} alt={g.alt} loading="lazy" className={`h-72 w-full rounded-2xl object-cover object-top sm:h-96 ${i % 2 ? "md:mt-10" : ""}`} data-testid={`founder-photo-${i}`} />
          ))}
        </div>
      </section>
    </div>
  );
};

export const Reviews = () => {
  const [items, setItems] = useState<any[] | null>(null);
  useEffect(() => {
    api.get("/reviews").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  const avg = items?.length ? items.reduce((a, r) => a + r.rating, 0) / items.length : 0;
  return (
    <div>
      <PageHeader eyebrow="Verified guests only" title="Guest Reviews" intro="Reviews can only be written by guests with a completed booking, and every review is moderated before it appears." />
      <div className="container-x py-14">
        {!items ? <Spinner /> : items.length === 0 ? (
          <EmptyState testid="reviews-empty" title="No reviews yet" body="We do not publish placeholder or invented reviews. Verified reviews from completed bookings will appear here." cta="Browse stays" to="/stays" />
        ) : (
          <>
            <p className="mb-8 flex items-center gap-2 text-lg" data-testid="reviews-average"><Star className="fill-gold text-gold" /> <span className="font-mono font-bold">{avg.toFixed(1)}</span> · {items.length} verified review{items.length > 1 ? "s" : ""}</p>
            <div className="columns-1 gap-6 md:columns-2 lg:columns-3">
              {items.map((r) => (
                <div key={r.id} className="card-lux mb-6 break-inside-avoid p-6" data-testid={`review-${r.id}`}>
                  <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={15} className={n <= r.rating ? "fill-gold text-gold" : "text-navy/20"} />)}</div>
                  <p className="mt-4 text-sm leading-relaxed text-navy/80">“{r.comment}”</p>
                  <p className="mt-4 text-sm font-semibold">{r.name}</p>
                  <p className="text-xs text-navy/50">{r.item_title} · {fmtDate(r.created_at)} · Verified booking</p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
